import { DEFAULT_AREA_COLORS } from './cellColorPalette';

/** 色分けの単位。API の `company_dispatch_cell_colors.mode` と同じ値 */
export type CellColorMode = 'none' | 'area' | 'prefecture';
/** 色分けの基準地点。API の `company_dispatch_cell_colors.basis` と同じ値 */
export type CellColorBasis = 'unloading' | 'loading';

/**
 * エリアと所属県。API が `GET` / `PUT /api/v5/dispatch_cell_colors` の `areas` で毎回返す。
 *
 * Why not FE に変換表の写しを置く: 区分の切り方を変えたときに、画面とデータで別々の
 * エリアを指す期間ができる（C2 で「変換表は API の 1 箇所だけ」と決めた）。
 */
export type CellColorArea = {
  name: string;
  prefectures: string[];
};

/**
 * セルの色分け設定のうち、会社が書き換える部分（`PUT /api/v5/dispatch_cell_colors` の payload）。
 *
 * Why not camelCase へ直す: 会社が付けた色のキー（区分名）は自由文字列で、値と一緒に
 * そのまま往復する。入れ物だけ camelCase にすると、1 つの型の中でキーの流儀が混ざる。
 */
export type DispatchCellColorSettings = {
  mode: CellColorMode;
  basis: CellColorBasis;
  /** エリア名 → '#rrggbb'。会社が変えた区分だけ入る */
  area_colors: Record<string, string>;
  /** 都道府県名 → '#rrggbb'。会社が変えた区分だけ入る */
  prefecture_colors: Record<string, string>;
};

/** GET / PUT のレスポンス。会社の設定に、読み取り専用のエリア表が付く */
export type DispatchCellColors = DispatchCellColorSettings & {
  areas: CellColorArea[];
};

/** 未登録の会社に API が返す設定と同じ既定値 */
export const DEFAULT_CELL_COLOR_SETTINGS: DispatchCellColorSettings = {
  mode: 'none',
  basis: 'unloading',
  area_colors: {},
  prefecture_colors: {},
};

/** 取得前に使う値。エリア表が空なので、都道府県の色は引けない（＝色なし）に倒れる */
export const DEFAULT_CELL_COLORS: DispatchCellColors = {
  ...DEFAULT_CELL_COLOR_SETTINGS,
  areas: [],
};

/**
 * 色を決めるのに要る運行の区分だけ。`LongHaulTrip` がそのまま渡せる形にしてある。
 * 降地は運行あたり複数ありうるので配列で受ける。
 */
export type CellColorTarget = {
  loadingArea: string | null;
  unloadingAreas: readonly string[];
  loadingPrefecture: string | null;
  unloadingPrefectures: readonly string[];
};

/** その県が属するエリア名。対応表に無ければ null */
export const areaOfPrefecture = (
  areas: readonly CellColorArea[],
  prefecture: string,
): string | null => areas.find((area) => area.prefectures.includes(prefecture))?.name ?? null;

/** エリアに塗る色。会社が変えた色 → 既定色 の順 */
export const areaColorOf = (
  settings: DispatchCellColorSettings,
  area: string,
): string | null => settings.area_colors[area] ?? DEFAULT_AREA_COLORS[area] ?? null;

/**
 * 都道府県に塗る色。会社が変えた県の色 → その県が属するエリアの色 の順。
 *
 * 県ごとの既定色は持たない。同じエリアの県には同じ色が配られる
 * （サンプル route-planner `CellColorSettingsDialog.tsx:122` と同じ・ユーザー判断 2026-09-28）。
 */
export const prefectureColorOf = (
  settings: DispatchCellColorSettings,
  areas: readonly CellColorArea[],
  prefecture: string,
): string | null => {
  const own = settings.prefecture_colors[prefecture];
  if (own) return own;

  const area = areaOfPrefecture(areas, prefecture);
  return area ? areaColorOf(settings, area) : null;
};

/**
 * 運行バーに塗る色。色分けが「なし」のときと、基準地点の区分が引けないときは null。
 *
 * 降地が複数ある運行は**最初の降地**で決める。長距離を走り切った先が最初の降地で、
 * 2 箇所目以降はその周辺を回ることが多く、「この便がどこへ向かうのか」をいちばん表すため。
 *
 * Why not 区分が引けない運行にも色を出す: 引けないのは住所を正規化できていない地点で、
 * 適当な色を当てると「エリアが決まっている運行」と見分けがつかなくなる。null を返して
 * 呼び出し側（バー）に今までどおりの黒を描かせる。
 */
export const resolveCellColor = (
  colors: DispatchCellColors,
  target: CellColorTarget,
): string | null => {
  if (colors.mode === 'none') return null;

  if (colors.mode === 'area') {
    const area = colors.basis === 'loading' ? target.loadingArea : target.unloadingAreas[0] ?? null;
    if (!area) return null;
    return areaColorOf(colors, area);
  }

  const prefecture = colors.basis === 'loading'
    ? target.loadingPrefecture
    : target.unloadingPrefectures[0] ?? null;
  if (!prefecture) return null;
  return prefectureColorOf(colors, colors.areas, prefecture);
};
